import React, { useState, useEffect, Fragment } from 'react';
import { Input, Table, message, Modal, Tag, Button, Tabs, Card, Avatar, Typography, Space, Divider } from "antd";
import { PlusOutlined, ExclamationCircleOutlined, GithubOutlined, BranchesOutlined, UserOutlined, TeamOutlined, CodeOutlined } from '@ant-design/icons';
import useWscTableList from '@/component/UseWscTableList';
import useWscCustomFormModal from '@/component/UseWscCustomForm';
import useWscCustomColumns from '@/component/UseWscCustomColumns';
import useRequest from '@/utils/useRequest';
import ColumnSettings from '@/component/ColumnSettings';

const { Title, Text, Paragraph } = Typography;

const initParams = {
  pagination: {
    current: 1,
    pageSize: 10
  },
  filters: {},
  sorter: null
};

const PipelineList = () => {
  const { request } = useRequest();
  const { showDynamicModal, hide, WscCustomFormModal } = useWscCustomFormModal();
  const { list, isLoading, pagination, setPage, setFilter, filters, setSorter, Refresh } = useWscTableList({
    url: "/api/v2/private/pipeline/list", 
    initParams: initParams
  });

  const { Refresh: refreshColumns, colsettings } = useWscCustomColumns({ key: "pipeline" });
  const [columns, setColumns] = useState([]);
  const [projectInfo, setProjectInfo] = useState({
    name: 'jymt-dotnet-server',
    description: '金域美堂 .NET 服务端项目',
    gitUrl: 'https://git.example.com/jymt/jymt-dotnet-server.git',
    leader: {
      name: '张三',
      avatar: 'https://zos.alipayobjects.com/rmsportal/ODTLcjxAfvqbxHnVXCYX.png'
    },
    members: [
      { name: '李四', avatar: 'https://zos.alipayobjects.com/rmsportal/ODTLcjxAfvqbxHnVXCYX.png' },
      { name: '王五', avatar: 'https://zos.alipayobjects.com/rmsportal/ODTLcjxAfvqbxHnVXCYX.png' },
      { name: '赵六', avatar: 'https://zos.alipayobjects.com/rmsportal/ODTLcjxAfvqbxHnVXCYX.png' }
    ],
    versions: {
      production: 'v1.2.3',
      testing: 'v1.3.0-beta'
    }
  });
  
  // 获取项目信息
  useEffect(() => {
    const fetchProjectInfo = async () => {
      try {
        const result = await request("/api/v2/private/project/info", "GET");
        if (result.success) {
          setProjectInfo(result.data);
        }
      } catch (error) {
        console.error("获取项目信息失败:", error);
      }
    };
    
    fetchProjectInfo();
  }, []);
  
  // 状态渲染函数
  const renderStatus = (status) => {
    const statusMap = {
      'passed': <Tag color="success">已通过</Tag>,
      'running': <Tag color="processing">运行中</Tag>,
      'waiting': <Tag color="default">等待中</Tag>,
      'failed': <Tag color="error">失败</Tag>
    };
    return statusMap[status] || <Tag>{status}</Tag>;
  };

  useEffect(() => {
    if (colsettings) {
      const defaultColumns = [
        {
          title: 'Status',
          dataIndex: 'status',
          render: (status) => renderStatus(status)
        },
        {
          title: 'Pipeline',
          dataIndex: 'pipeline',
        },
        {
          title: 'Commit',
          dataIndex: 'commit',
          render: (commit) => (
            <div>
              <div>{commit.hash}</div>
              <div>{commit.message}</div>
            </div>
          )
        },
        {
          title: 'Stages',
          dataIndex: 'stages',
          render: (stages) => (
            <div>
              {stages.map((stage, index) => (
                <Tag key={index} color={stage.status === 'success' ? 'green' : 'red'}>
                  {stage.name}
                </Tag>
              ))}
            </div>
          )
        },
        {
          title: '耗时',
          dataIndex: 'duration',
        },
        {
          title: '执行时间',
          dataIndex: 'executedAt',
        }
      ];

      setColumns(defaultColumns.concat(ExtendColumns));
    }
  }, [colsettings]);

  const ExtendColumns = [
    {
      title: <div>操作 <ColumnSettings topic="pipeline" colsettings={colsettings} list={list} onFinish={refreshColumns} /></div>,
      dataIndex: 'id',
      render: (id, record) => (
        <Fragment>
          <Button type="link" onClick={() => showDynamicModal(53, record)}>
            查看详情
          </Button>
          <Button type="link" onClick={() => handleRerun(record)}>
            重新运行
          </Button>
        </Fragment>
      ),
    },
  ];

  const handleRerun = async (record) => {
    try {
      const result = await request("/api/v2/private/pipeline/rerun", "POST", "json", { id: record.id });
      if (result.success) {
        message.success("重新运行成功");
        Refresh();
      } else {
        Modal.error({ content: result.message });
      }
    } catch (error) {
      Modal.error({ content: error.message });
    }
  };

  return (
    <div className='bg-white p-4'>
      {/* 项目信息概览 */}
      <div className="mb-6">
        <Card bordered={false} className="shadow-sm">
          <div className="flex flex-col md:flex-row justify-between">
            {/* 项目基本信息 */}
            <div className="flex-1 mb-4 md:mb-0">
              <Title level={3}>{projectInfo.name}</Title>
              <Paragraph className="text-gray-500">{projectInfo.description}</Paragraph>
              
              <Space className="mt-4">
                <div className="flex items-center">
                  <GithubOutlined className="mr-2 text-lg" />
                  <Text copyable>{projectInfo.gitUrl}</Text>
                </div>
              </Space>
              
              <Divider />
              
              <div className="flex flex-wrap gap-8">
                <div>
                  <Text type="secondary">正式版本</Text>
                  <div className="mt-1">
                    <Tag color="green" icon={<CodeOutlined />}>{projectInfo.versions.production}</Tag>
                  </div>
                </div>
                
                <div>
                  <Text type="secondary">测试版本</Text>
                  <div className="mt-1">
                    <Tag color="blue" icon={<CodeOutlined />}>{projectInfo.versions.testing}</Tag>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 团队信息 */}
            <div className="md:ml-8 md:border-l md:pl-8 flex flex-col">
              <div className="mb-4">
                <Text type="secondary">项目负责人</Text>
                <div className="mt-2 flex items-center">
                  <Avatar src={projectInfo.leader.avatar} size="large" />
                  <Text className="ml-2">{projectInfo.leader.name}</Text>
                </div>
              </div>
              
              <div>
                <Text type="secondary">项目成员</Text>
                <div className="mt-2">
                  <Avatar.Group maxCount={5}>
                    {projectInfo.members.map((member, index) => (
                      <Avatar key={index} src={member.avatar} title={member.name} />
                    ))}
                  </Avatar.Group>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      
      {/* 操作按钮 */}
      <div className="flex justify-end mb-4">
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showDynamicModal(51, {})}>
            运行流水线
          </Button>
          <Button onClick={() => showDynamicModal(52, {})}>
            清除Runner缓存
          </Button>
        </Space>
      </div>

      {/* 流水线列表 */}
      <Tabs
        defaultActiveKey="all"
        items={[
          { key: 'all', label: `全部 ${pagination.total || 0}` },
          { key: 'waiting', label: `等待中 0` },
          { key: 'running', label: `运行中 0` },
          { key: 'completed', label: `已完成 ${pagination.total || 0}` },
        ]}
      />

      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={list}
        columns={columns}
        pagination={pagination}
        onChange={(pagination, filters, sorter) => {
          setPage(pagination.current);
          setFilter({ ...filters });
          setSorter(sorter);
        }}
      />
    </div>
  );
};

export default PipelineList;